import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicFormField } from '../../../../interfaces/dynamic-form';
import { DynamicFormService } from '../../../../services/dynamic-form.service';

@Component({
  selector: 'app-dynamic-file-upload',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="form-group">
      <label>
        {{ field.etichetta }}
        <span class="required" *ngIf="field.obbligatorio">*</span>
      </label>

      <div class="upload-area" *ngIf="!uploadedId && !uploading">
        <input
          type="file"
          [accept]="acceptString"
          (change)="onFileSelected($event)"
          #fileInput
        />
        <div class="upload-placeholder" (click)="fileInput.click()">
          <span class="upload-icon">+</span>
          <span class="upload-text">Clicca per caricare</span>
          <span class="upload-hint" *ngIf="maxSizeMB">Max {{ maxSizeMB }}MB</span>
        </div>
      </div>

      <div class="uploading" *ngIf="uploading">
        <span class="spinner"></span> Caricamento in corso...
      </div>

      <div class="uploaded" *ngIf="uploadedId && !uploading">
        <span class="success-icon">&#10003;</span>
        <span class="filename">{{ uploadedFilename }}</span>
        <button class="remove-btn" (click)="removeFile()">Rimuovi</button>
      </div>

      <div class="error" *ngIf="errorMessage">{{ errorMessage }}</div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .form-group {
      margin-bottom: 20px;
      label {
        display: block;
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
        color: var(--text, #e0e0e0);
      }
      .required { color: #f59e0b; }
    }
    .upload-area {
      input[type="file"] { display: none; }
      .upload-placeholder {
        border: 2px dashed var(--border, #333);
        padding: 30px;
        text-align: center;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
        &:hover { border-color: var(--accent, #ffd65b); }
      }
      .upload-icon { font-size: 24px; color: var(--text-muted, #999); }
      .upload-text { color: var(--text, #e0e0e0); font-size: 14px; }
      .upload-hint { color: #666; font-size: 12px; }
    }
    .uploading {
      padding: 15px;
      color: var(--text-muted, #999);
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .spinner {
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2px solid var(--border, #333);
      border-top-color: var(--accent, #ffd65b);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .uploaded {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px;
      background: rgba(16, 185, 129, 0.1);
      border: 1px solid rgba(16, 185, 129, 0.3);
      .success-icon { color: #10b981; font-size: 18px; }
      .filename { color: var(--text, #e0e0e0); font-size: 14px; }
      .remove-btn {
        margin-left: auto;
        background: none;
        border: 1px solid #666;
        color: var(--text-muted, #999);
        padding: 4px 10px;
        cursor: pointer;
        font-size: 12px;
        &:hover { border-color: #f59e0b; color: #f59e0b; }
      }
    }
    .error {
      color: #ef4444;
      font-size: 13px;
      margin-top: 6px;
    }
  `],
})
export class DynamicFileUploadComponent {
  @Input() field!: DynamicFormField;
  @Input() eventoId = '';
  @Output() fileUploaded = new EventEmitter<{ nomeCampo: string; documentoId: string }>();
  @Output() fileRemoved = new EventEmitter<string>();

  private formService = inject(DynamicFormService);

  uploading = false;
  uploadedId = '';
  uploadedFilename = '';
  errorMessage = '';

  get acceptString(): string {
    return this.field.tipiAccettati?.join(',') || 'image/jpeg,image/png,application/pdf';
  }

  get maxSizeMB(): number {
    return Math.round((this.field.dimensioneMassimaBytes || 10485760) / (1024 * 1024));
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.errorMessage = '';

    const maxSize = this.field.dimensioneMassimaBytes || 10485760;
    if (file.size > maxSize) {
      this.errorMessage = `File troppo grande. Dimensione massima: ${this.maxSizeMB}MB.`;
      return;
    }

    this.uploading = true;

    this.formService
      .uploadFile(
        file,
        this.field.nome,
        this.eventoId,
        this.field.tipiAccettati,
        this.field.dimensioneMassimaBytes
      )
      .subscribe({
        next: (response) => {
          this.uploading = false;
          if (response.success && response.documentId) {
            this.uploadedId = response.documentId;
            this.uploadedFilename = file.name;
            this.fileUploaded.emit({
              nomeCampo: this.field.nome,
              documentoId: response.documentId,
            });
          } else {
            this.errorMessage = response.error || 'Errore nel caricamento';
          }
        },
        error: (err) => {
          this.uploading = false;
          this.errorMessage = err?.error?.error || 'Errore nel caricamento del file.';
        },
      });
  }

  removeFile(): void {
    this.fileRemoved.emit(this.field.nome);
    this.uploadedId = '';
    this.uploadedFilename = '';
  }
}
